/**
 * What each confirm dialog of the Intro video tab says (spec 2026-10-23 §5:
 * "nothing irreversible without a confirmation"). Pure, so the words are
 * tested (tests/unit/admin/app-experience-intro-state.test.ts) and the tab
 * only wires them.
 *
 * Each dialog says what will happen to members, and when. The public view
 * (GET /v1/app-experience) is cached for up to 60 s on the server and may be
 * kept for up to 60 s more by the phone (max-age=60), so a change can take
 * about 2 minutes to reach the app.
 */
import type { IntroPublishState, RestorePlan, SwitchState } from './intro-state';

export interface ConfirmRequest {
  title: string;
  /** What will happen, one sentence per item. */
  lines: string[];
  /** Shown with a warning icon; never block. */
  warnings?: string[];
  confirmLabel: string;
  /** Null = no note field (e.g. Discard, which publishes nothing). */
  noteLabel: string | null;
  destructive?: boolean;
  /** The toast after a success. */
  success: string;
}

export const DELAY = 'The change can take about 2 minutes to reach the app.';
const NOTE_LABEL = 'Note (optional)';

export function switchRequest(sw: SwitchState, dirty: boolean): ConfirmRequest {
  if (sw.on) {
    return {
      title: 'Turn the intro off?',
      lines: ['New installs do not see the intro video.', 'The videos stay saved. You can turn the intro on again at any time.', DELAY],
      confirmLabel: 'Turn off',
      noteLabel: NOTE_LABEL,
      destructive: true,
      success: `The intro is off. ${DELAY}`,
    };
  }
  return {
    title: 'Turn the intro on?',
    lines: [
      'New installs see the intro video before sign-up. It plays on app 1.0.5 and later.',
      ...(dirty ? ['Your changes that are not published stay as a draft. This turns on the newest published version only.'] : []),
      DELAY,
    ],
    confirmLabel: 'Turn on',
    noteLabel: NOTE_LABEL,
    success: `The intro is on. ${DELAY}`,
  };
}

export function publishRequest(pub: IntroPublishState, liveOn: boolean): ConfirmRequest {
  return {
    title: 'Publish this version?',
    lines: liveOn
      ? ['New installs get this version.', DELAY]
      : ['The intro is off. This saves the version. Members do not see it until you turn the intro on.'],
    warnings: pub.warnings,
    confirmLabel: 'Publish',
    noteLabel: NOTE_LABEL,
    success: liveOn ? `The new version is published. ${DELAY}` : 'The version is saved. The intro is still off.',
  };
}

export function restoreRequest(version: number, plan: RestorePlan, liveOn: boolean): ConfirmRequest {
  // Members notice a restore only when the intro is on before or after it.
  const reaches = plan.config.enabled || liveOn;
  return {
    title: `Restore version ${version}?`,
    lines: [`This publishes version ${version} again as the newest version.`, ...plan.effects, ...(reaches ? [DELAY] : [])],
    confirmLabel: `Restore version ${version}`,
    noteLabel: NOTE_LABEL,
    success: `Version ${version} is restored.`,
  };
}

export function discardRequest(): ConfirmRequest {
  return {
    title: 'Discard your changes?',
    lines: ['The page goes back to the newest published version.', 'A video that you uploaded and did not publish is removed from this page.'],
    confirmLabel: 'Discard changes',
    noteLabel: null,
    destructive: true,
    success: 'Your changes are discarded.',
  };
}
